





















































import { Component, Vue, Watch } from 'vue-property-decorator';
import { IPriceCategory, IPriceTier, IValidations } from '@/interfaces';
import { PriceCategoryValidator, PriceTierValidator } from '@/validators';
import { AjaxHelper } from '@/AjaxHelper';
import { showAxiosErrorMessage } from '@/utils';

import Field from '@/components/Field.vue';
import TextField from '@/components/TextField.vue';
import NumberField from '@/components/NumberField.vue';
import Btn from '@/components/Btn.vue';
import List from '@/components/List.vue';
import ListItem from '@/components/ListItem.vue';
import TextareaField from '@/components/TextareaField.vue';

@Component({
  name: 'admin-price-category-page',
  components: { Field, TextField, NumberField, Btn, List, ListItem, TextareaField },
  beforeRouteEnter: async (to, from, next) => {
    let model: IPriceCategory = { name: '', description: '', priceTiers: [] };
    let isNew = true;
    if (to.params.id && to.params.id !== 'new') {
      model = await AjaxHelper.get<IPriceCategory>(`/PriceCategory/${to.params.id}`);
      isNew = false;
    }
    next(vm => {
      (vm as any).model = model;
      (vm as any).isNew = isNew;
    });
  }
})
export default class AdminPriceCategoryComponent extends Vue {
  public isNew = true;
  public saving = false;
  public model: IPriceCategory = { name: '', description: '', priceTiers: [] };

  public modelShowValidations = false;
  public modelValidations: IValidations<IPriceCategory> | null = null;
  public get tierValidations(): (IValidations<IPriceTier> | null)[] { return this.model.priceTiers?.map(x => this.modelShowValidations ? this.generatePriceTierValidations(x) : null) ?? []; }

  public categoryValidator = new PriceCategoryValidator();
  public tierValidator = new PriceTierValidator();

  public get emptyPricingTierValidation(): string[] {
    if (this.modelValidations !== null && this.modelValidations.priceTiers !== undefined) {
      return ['border-red-800', 'bg-red-100', 'text-red-800'];
    }
    return ['border-blue-800', 'bg-blue-100', 'text-blue-800']
  }

  public get emptyPricingTierMessage(): string {
    const validations = this.getValidationText(this.modelValidations, 'priceTiers');
    if (validations.length > 0) {
      return validations[0];
    }
    return 'No Pricing Tiers Found';
  }

  /**
   * Add a price tier to the cateogry
   */
  public addPriceTier() {
    if (this.model.priceTiers !== undefined) {
      this.model.priceTiers.push({ name: '', price: 0 });
    }
  }

  /**
   * Remove a pricing tier from the category
   * 
   * @param item The pricing tier we want to remove
   */
  public removeTier(item: IPriceTier) {
    const index = this.model.priceTiers?.indexOf(item) ?? -1;
    if (index > -1 && this.model.priceTiers !== undefined) {
      this.model.priceTiers.splice(index, 1);
    }
  }

  /**
   * Method is meant to save the model to the database
   */
  public async save() {
    try {
      this.saving = true;
      const validations = this.categoryValidator.isValid(this.model);
      if (validations === true) {
        this.modelShowValidations = false;
        this.modelValidations = null;

        if (this.isNew) {
          this.model = await AjaxHelper.post('/pricecategory', this.model);
          this.$alert.success('Successfully Created Category');
          this.isNew = false;
        } else {
          this.model = await AjaxHelper.put('/pricecategory', this.model);
          this.$alert.success('Successfully Updated Category');
        }

      } else {
        this.modelValidations = validations;
        this.modelShowValidations = true;
      }
    } catch (ex) {
      showAxiosErrorMessage(ex);
    } finally {
      this.saving = false;
    }
  }

  /**
   * Helper method to get the validation text that should be displayed to the user when the validation is triggered
   * 
   * @param key The key we want to get the validations from
   */
  public getValidationText<T>(validations: IValidations<T> | null, key: keyof T) {
    return (validations !== null ? validations[key] : null) ?? '';
  }

  /**
   * Helper method is meant to watch the model and make updates based on if the model can be saved
   * 
   * @param newModel The model that has been updated
   */
  @Watch('model', { deep: true })
  public onModelChange(newModel: IPriceCategory) {
    if (this.modelShowValidations) {
      const validations = this.categoryValidator.isValid(newModel);
      this.modelValidations = validations === true ? null : validations;
    }
  }

  /**
   * Helper method to generate validations and return null or the validations for use by the component
   * 
   * @param session The current session we are handling
   */
  private generatePriceTierValidations(priceTier: IPriceTier) {
    const validations = this.tierValidator.isValid(priceTier);
    return validations === true ? null : validations;
  }
}
